import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getRelease, getDSPServices, getSocialPlatforms } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { FaSpotify, FaApple, FaYoutube, FaAmazon, FaSoundcloud, FaInstagram, FaTwitter, FaFacebook, FaTiktok, FaBandcamp, FaExternalLinkAlt, FaArrowLeft, FaHeadphones } from "react-icons/fa";
import { SiTidal } from "react-icons/si";

const DSP_ICONS = {
  spotify: FaSpotify,
  apple: FaApple,
  youtube: FaYoutube,
  tidal: SiTidal,
  amazon: FaAmazon,
  deezer: FaHeadphones,
  soundcloud: FaSoundcloud,
};

const SOCIAL_ICONS = {
  instagram: FaInstagram,
  twitter: FaTwitter,
  facebook: FaFacebook,
  tiktok: FaTiktok,
  bandcamp: FaBandcamp,
  youtube: FaYoutube,
};

const DSP_COLORS = {
  spotify: "bg-zinc-800 hover:bg-zinc-700",
  apple: "bg-zinc-800 hover:bg-zinc-700",
  youtube: "bg-zinc-800 hover:bg-zinc-700",
  tidal: "bg-zinc-800 hover:bg-zinc-700",
  amazon: "bg-zinc-800 hover:bg-zinc-700",
  deezer: "bg-zinc-800 hover:bg-zinc-700",
  soundcloud: "bg-zinc-800 hover:bg-zinc-700",
};

const SOCIAL_COLORS = {
  instagram: "bg-zinc-800 hover:bg-zinc-700",
  twitter: "bg-zinc-800 hover:bg-zinc-700",
  facebook: "bg-zinc-800 hover:bg-zinc-700",
  tiktok: "bg-zinc-800 hover:bg-zinc-700",
  bandcamp: "bg-zinc-800 hover:bg-zinc-700",
  youtube: "bg-zinc-800 hover:bg-zinc-700",
};

export default function ReleaseDetail() {
  const { id } = useParams();
  const [release, setRelease] = useState(null);
  const [dspServices, setDspServices] = useState([]);
  const [socialPlatforms, setSocialPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [releaseRes, dspRes, socialRes] = await Promise.all([
          getRelease(id),
          getDSPServices(),
          getSocialPlatforms(),
        ]);
        setRelease(releaseRes.data);
        setDspServices(dspRes.data);
        setSocialPlatforms(socialRes.data);
      } catch (error) {
        console.error("Error fetching release:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  const getDSPInfo = (serviceId) => {
    const service = dspServices.find((s) => s.id === serviceId);
    return {
      name: service?.name || "Listen",
      icon: DSP_ICONS[service?.icon] || FaExternalLinkAlt,
      color: DSP_COLORS[service?.icon] || "bg-accent-primary hover:bg-accent-secondary",
    };
  };

  const getSocialInfo = (platformId) => {
    const platform = socialPlatforms.find((p) => p.id === platformId);
    return {
      name: platform?.name || "Follow",
      icon: SOCIAL_ICONS[platform?.icon] || FaExternalLinkAlt,
      color: SOCIAL_COLORS[platform?.icon] || "bg-zinc-700 hover:bg-zinc-600",
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8" data-testid="release-detail-loading">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-32 mb-8" />
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-square rounded-xl" />
            <div className="space-y-4">
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
              <div className="flex gap-2 mt-6">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-32" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!release) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="release-not-found">
        <div className="text-center">
          <h2 className="text-2xl font-syne font-bold text-white mb-4">Release Not Found</h2>
          <Link to="/">
            <Button variant="outline" data-testid="back-to-home-btn">
              <FaArrowLeft className="mr-2" /> Back to Releases
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const embedUrl = getYouTubeEmbedUrl(release.youtube_url);

  return (
    <div className="min-h-screen bg-background" data-testid="release-detail-page">
      {/* Back Button */}
      <div className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" data-testid="back-link">
            <Button variant="ghost" className="text-zinc-400 hover:text-white">
              <FaArrowLeft className="mr-2" /> Back
            </Button>
          </Link>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Release Header */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Artwork */}
            <div className="relative group">
              <div className="aspect-square rounded-xl overflow-hidden border border-zinc-800 glow">
                <img
                  src={release.artwork_url}
                  alt={release.title}
                  className="w-full h-full object-cover"
                  data-testid="release-detail-artwork"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex flex-col justify-center">
              <p className="text-accent-primary text-sm font-space uppercase tracking-widest mb-2">
                {formatDate(release.release_date)}
              </p>
              <h1 className="text-3xl md:text-4xl font-syne font-extrabold text-white mb-2" data-testid="release-detail-title">
                {release.title}
              </h1>
              <p className="text-xl text-zinc-400 mb-6" data-testid="release-detail-artist">{release.artist}</p>

              {/* DSP Links */}
              {release.dsp_links && release.dsp_links.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-space uppercase tracking-widest text-zinc-500 mb-3">Stream Now</h3>
                  <div className="flex flex-wrap gap-2" data-testid="release-detail-dsp-links">
                    {release.dsp_links.map((link, idx) => {
                      const { name, icon: Icon, color } = getDSPInfo(link.service_id);
                      return (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium transition-all ${color}`}
                          data-testid={`detail-dsp-${link.service_id}`}
                        >
                          <Icon className="w-4 h-4" />
                          {name}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {release.social_links && release.social_links.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-space uppercase tracking-widest text-zinc-500 mb-3">Follow Artist</h3>
                  <div className="flex flex-wrap gap-2" data-testid="release-detail-social-links">
                    {release.social_links.map((link, idx) => {
                      const { name, icon: Icon, color } = getSocialInfo(link.platform_id);
                      return (
                        <a
                          key={idx}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-white text-sm font-medium transition-all ${color}`}
                          data-testid={`detail-social-${link.platform_id}`}
                        >
                          <Icon className="w-4 h-4" />
                          {name}
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* External Links - Grouped by Header */}
              {release.external_links && release.external_links.length > 0 && (
                <div className="space-y-4" data-testid="release-detail-external-links">
                  {/* Group links by header */}
                  {Object.entries(
                    release.external_links.reduce((acc, link) => {
                      const header = link.header || "More Links";
                      if (!acc[header]) acc[header] = [];
                      acc[header].push(link);
                      return acc;
                    }, {})
                  ).map(([header, links], groupIdx) => (
                    <div key={groupIdx}>
                      <h3 className="text-sm font-space uppercase tracking-widest text-zinc-500 mb-3">{header}</h3>
                      <div className="flex flex-wrap gap-2">
                        {links.map((link, idx) => (
                          <a
                            key={idx}
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-medium transition-all"
                            data-testid={`detail-external-${groupIdx}-${idx}`}
                          >
                            <FaExternalLinkAlt className="w-3 h-3" />
                            {link.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* YouTube Video */}
          {embedUrl && (
            <div className="mb-12">
              <h3 className="text-sm font-space uppercase tracking-widest text-zinc-500 mb-4">Watch Video</h3>
              <div className="aspect-video rounded-xl overflow-hidden border border-zinc-800" data-testid="release-youtube-embed">
                <iframe
                  src={embedUrl}
                  title={release.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
